<template>
  <div id="UserRegister">
    <div class="title">{{ componentText.title }}</div>

    <template v-if="!preventSso">
      <ThirdPartyLogin
        :show-ms-sso="showMsSso"
        :show-google-sso="showGoogleSso"
      />
      <div class="orDivider" v-if="showGoogleSso || showMsSso">
        <div class="divider"></div>
        <div class="or">or</div>
        <div class="divider"></div>
      </div>
    </template>

    <b-form>
      <div>
        <b-form-group
          id="input-group-3"
          :label="componentText.email"
          label-for="signupform-username"
          ><b-form-input
            type="text"
            :class="{ invalid: !validEmail }"
            id="signupform-username"
            v-model="newUser.username"
            :disabled="isEmailDisabled"
            required
          ></b-form-input>
          <span
            v-if="newUser.username && !validEmail"
            class="error-text failed ml-2"
            >Must be a valid email address</span
          >
        </b-form-group>

        <div class="nameHolder">
          <b-form-group
            id="input-group-1"
            :label="componentText.firstName"
            label-for="signupform-firstname"
            ><b-form-input
              type="text"
              id="signupform-firstname"
              v-model="newUser.firstname"
              :disabled="isFirstNameDisabled"
              :class="{ invalid: newUser.firstname.length < 1 }"
              required
            ></b-form-input>
          </b-form-group>
          <div></div>
          <b-form-group
            :label="componentText.lastName"
            id="input-group-2"
            label-for="signupform-lastname"
          >
            <b-form-input
              type="text"
              id="signupform-lastname"
              v-model="newUser.lastname"
              :class="{ invalid: newUser.lastname.length < 1 }"
              :disabled="isLastNameDisabled"
              required
            ></b-form-input>
          </b-form-group>
        </div>

        <b-form-group
          id="input-group-5"
          :label="componentText.password"
          label-for="newPassword"
        >
          <div class="password-wrap">
            <b-form-input
              :type="passwordFieldType"
              id="newPassword"
              v-model="$v.newPassword.$model"
              required
              :class="{ invalid: $v.newPassword.$error }"
            ></b-form-input>
            <div class="eye" @click="switchVisibility">
              <font-awesome-icon
                v-if="passwordFieldType === 'password'"
                icon="eye"
              ></font-awesome-icon>
              <font-awesome-icon v-else icon="eye-slash"></font-awesome-icon>
            </div>
          </div>
        </b-form-group>

        <div class="validationBox">
          <span :class="[tenChars ? 'passed' : 'failed']"
            ><font-awesome-icon
              icon="times"
              v-if="!tenChars"
            /><font-awesome-icon icon="check" v-else></font-awesome-icon> At
            least 10 characters</span
          ><br />
          <span :class="[upperCase ? 'passed' : 'failed']"
            ><font-awesome-icon
              icon="times"
              v-if="!upperCase"
            ></font-awesome-icon
            ><font-awesome-icon icon="check" v-else></font-awesome-icon> At
            least 1 uppercase letter</span
          ><br />
          <span :class="[lowerCase ? 'passed' : 'failed']"
            ><font-awesome-icon
              icon="times"
              v-if="!lowerCase"
            ></font-awesome-icon
            ><font-awesome-icon icon="check" v-else></font-awesome-icon> At
            least 1 lowercase letter</span
          ><br />
          <span :class="[contNumber ? 'passed' : 'failed']"
            ><font-awesome-icon
              icon="times"
              v-if="!contNumber"
            ></font-awesome-icon
            ><font-awesome-icon icon="check" v-else></font-awesome-icon> At
            least 1 number</span
          ><br />
          <span :class="[specChar ? 'passed' : 'failed']"
            ><font-awesome-icon
              icon="times"
              v-if="!specChar"
            ></font-awesome-icon
            ><font-awesome-icon icon="check" v-else></font-awesome-icon> At
            least 1 special character</span
          >
        </div>

        <b-form-group
          id="input-group-4"
          :label="componentText.orgCode"
          label-for="signupform-org_code"
        >
          <b-form-input
            type="text"
            id="signupform-org_code"
            v-model="newUser.org_code"
            @input="onOrganisationCodeInput"
            @change="getOrgLocations"
            required
            :class="{ invalid: !isOrgCodeValid }"
            @focus="isOrgCodeFocused = true"
            @blur="onOrganisationCodeBlur"
            :disabled="fixedCode || isSignUpLoading"
          ></b-form-input>
          <span
            v-if="
              newUser.org_code &&
                !isOrgCodeValid &&
                !isOrgCodeFocused &&
                !isGetOrgLocationsLoading
            "
            class="error-text failed ml-2"
            >Must be a valid organisation code</span
          >
        </b-form-group>

        <b-form-group
          v-if="landgOrganizations && landgOrganizations.length"
          :label="componentText.org"
          label-for="custom-org-input"
        >
          <b-input-group class="position-relative custom-org-input">
            <i class="fa fa-chevron-down" aria-hidden="true"></i>
            <b-form-input
              id="custom-org-input"
              v-model="selectedOrganisation.organisation_name"
              @input="filterOptions"
              @focus="showDropdown = true"
              placeholder="Type or select..."
              autocomplete="off"
            ></b-form-input>
            <ul
              v-if="showDropdown && filteredOptions.length"
              class="custom-dropdown-list"
            >
              <li
                v-for="option in filteredOptions"
                :key="option.id"
                @click.stop="selectOption(option)"
              >
                {{ option.organisation_name }}
              </li>
            </ul>
          </b-input-group>
        </b-form-group>

        <div
          class="nameHolder"
          v-if="
            organisation &&
              (organisation.locations.length > 1 ||
                (organisation.member_levels &&
                  organisation.member_levels !== 'null' &&
                  organisation.member_levels.length > 1))
          "
        >
          <b-form-group
            id="input-group-7"
            :label="componentText.location"
            label-for="signupform-locations"
            v-if="organisation && organisation.locations.length > 1"
            ><b-form-select
              id="signupform-locations"
              v-model="newUser.location"
              class="form-control"
              required
              :class="{ invalid: newUser.location == null }"
            >
              <option :value="null" disabled>Select a location... </option>
              <option
                v-for="location in organisation.locations"
                :value="location.location_id"
                :key="'location' + location.location_id"
                >{{ location.location_name }}</option
              >
            </b-form-select>
          </b-form-group>
          <div v-if="organisation && organisation.locations.length > 1"></div>
          <b-form-group
            v-if="
              organisation &&
                organisation.member_levels &&
                organisation.member_levels !== 'null' &&
                organisation.member_levels.length > 1
            "
            id="input-group-8"
            :label="componentText.memberLevel"
            label-for="signupform-member_level"
          >
            <b-form-select
              id="signupform-member_level"
              class="form-control"
              v-model="newUser.member_level"
              :options="organisation.member_levels"
              required
            ></b-form-select>
          </b-form-group>
        </div>

        <b-form-group
          v-if="isLoadingTimezones || timezoneOptions.length > 0"
          id="input-group-timezone"
          :label="componentText.timezone"
          label-for="signupform-timezone-input"
        >
          <p
            v-if="isLoadingTimezones"
            class="timezone-loading"
            aria-live="polite"
          >
            <b-spinner small label="Spinning"></b-spinner>
            <span>{{ componentText.timezoneLoading }}</span>
          </p>
          <template v-else>
            <v-select
              id="signupform-timezone"
              input-id="signupform-timezone-input"
              v-model="selectedTimezone"
              :options="timezoneOptions"
              :closeOnSelect="true"
              :clearable="false"
              :placeholder="componentText.timezonePlaceholder"
              label="label"
              @input="onTimezoneChange"
            ></v-select>
            <small class="timezone-help">{{
              componentText.timezoneHelp
            }}</small>
          </template>
        </b-form-group>

        <div class="mobileHolder" v-if="signupTwoFactorEnabled">
          <b-form-group
            id="input-group-country-code"
            label="Country Code"
            label-for="signupform-country_code"
          >
            <b-form-select
              id="signupform-country_code"
              class="form-control countryCode"
              v-model="countryCode"
            >
              <b-form-select-option
                v-for="item in countryCodes"
                :value="item.dial_code"
                :key="item.code"
                >{{ item.name }} - {{ item.dial_code }}</b-form-select-option
              >
            </b-form-select>
          </b-form-group>
          <div></div>
          <b-form-group
            id="input-group-mobile"
            label="Mobile Number"
            label-for="signupform-mobile"
          >
            <b-form-input
              type="text"
              id="signupform-mobile"
              v-model="mobile"
              @keydown="editPhone"
              :class="{ invalid: signupTwoFactorEnabled && !mobile }"
            ></b-form-input>
          </b-form-group>
        </div>

        <div class="termsHolder">
          <div
            class="checkBoxHolder"
            @click="newUser.terms = !newUser.terms"
            :class="{ accepted: newUser.terms }"
          >
            <font-awesome-icon
              size="lg"
              icon="check"
              v-if="newUser.terms"
            ></font-awesome-icon>
          </div>
          <div class="terms">
            {{ componentText.agreePrivacy }}
            <a target="_blank" :href="privacyPolicyLink">{{
              componentText.privacy
            }}</a>
            {{ componentText.and }}
            <a target="_blank" href="/terms-and-conditions">
              {{ componentText.tcs }}</a
            >
          </div>
        </div>
      </div>
      <div
        id="signup-button"
        class="submit-form d-flex align-items-center justify-content-center"
      >
        <b-button
          @click="signUp"
          class="py-3 signUpBtn"
          :disabled="signUpDisabled || isSignUpLoading"
          ><span></span>
          <span>{{ componentText.buttonText }}</span>
          <i class="fa fa-arrow-right" aria-hidden="true"></i>
        </b-button>
      </div>
      <b-tooltip
        v-if="signUpDisabled"
        target="signup-button"
        triggers="hover"
        custom-class="stats-tooltip"
        >{{ componentText.validate }}</b-tooltip
      >
    </b-form>
    <div class="new-here-btn">
      <a @click="$emit('switchForm')" class="new-here-text">{{
        componentText.hasAccount
      }}</a>
    </div>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, sameAs } from "vuelidate/lib/validators";
import axios from "axios";
import config from "@/config/constants";
import { mapGetters } from "vuex";
import ThirdPartyLogin from "@/components/NotLoggedIn/ThirdPartyLogin.vue";
import LanguageSelect from "@/components/LanguageSelect.vue";
import country_codes from "@/assets/country_codes.json";
import { isAustralianApiUrl } from "@journeys/utils/helpers";
import {
  buildTwoFactorRoute,
  ensureE164Phone,
  sendTwoFactorCode,
} from "@/utils/twoFactor/twoFactor";
import {
  handleSignupMfaResponse,
  MFA_CHALLENGE_MISSING_MESSAGE,
} from "@/utils/twoFactor/signupMfa";
import {
  fetchTimezones,
  findTimezoneOption,
  isTimezoneSupportEnabled,
  regionalDefaultTimezone,
} from "@/utils/timezone/timezone";
import { trackEvent } from "@/utils/analytics/analytics";
import { SIGNUP_TIMEZONE_SELECTED } from "@/utils/analyticsEvents";

export default {
  name: "RegisterUser",
  components: { LanguageSelect, ThirdPartyLogin },
  mixins: [validationMixin],
  data() {
    return {
      user: {
        email: "",
      },
      newUser: {
        org_code: "",
        location: null,
        terms: false,
        firstname: "",
        lastname: "",
        username: "",
      },
      isSignUp: false,
      organisation: false,
      fixedCode: false,
      locations: [],
      timezoneOptions: [],
      selectedTimezone: null,
      isLoadingTimezones: false,
      signUpError: false,
      popUpContent: {},
      passwordFieldType: "password",
      newPassword: "",
      repeatPassword: "",
      tenChars: false,
      upperCase: false,
      lowerCase: false,
      contNumber: false,
      specChar: false,
      pw_flag: false,
      locationValid: true,
      isOrgCodeValid: false,
      preventSso: true,
      selectedOrganisation: {},
      showDropdown: true,
      filteredOptions: [],
      isLandgClient: false,
      landgOrganizations: [],
      referralId: null,
      referralData: null,
      isSignUpLoading: false,
      isEmailDisabled: false,
      isFirstNameDisabled: false,
      isLastNameDisabled: false,
      isOrgCodeFocused: false,
      isGetOrgLocationsLoading: false,
      mobile: null,
      countryCode: "+353",
      countryCodes: [],
      // Local to this form — do not use client.two_factor_enabled from branding.
      // That flag is the portal client, not the org the member is joining.
      signupTwoFactorEnabled: false,
      orgLookupGeneration: 0,
    };
  },
  validations: {
    newPassword: {
      required,
      minLength: function(value) {
        this.tenChars = value.length > 9;
        return value.length > 9;
      },
      containsUppercase: function(value) {
        this.upperCase = /[A-Z]/.test(value);
        return /[A-Z]/.test(value);
      },
      containsLowercase: function(value) {
        this.lowerCase = /[a-z]/.test(value);
        return /[a-z]/.test(value);
      },
      containsNumber: function(value) {
        this.contNumber = /[0-9]/.test(value);
        return /[0-9]/.test(value);
      },
      containsSpecial: function(value) {
        this.specChar = /[^a-zA-Z0-9\-\/]/.test(value);
        return /[^a-zA-Z0-9\-\/]/.test(value);
      },
    },
  },
  mounted() {
    // Surface the most common markets first; the rest stay in the JSON's order.
    const priorityCodes = ["IE", "GB", "AU", "NZ"];
    const prioritised = priorityCodes
      .map((code) => country_codes.find((item) => item.code === code))
      .filter(Boolean);
    const remainder = country_codes.filter(
      (item) => !priorityCodes.includes(item.code),
    );
    this.countryCodes = [...prioritised, ...remainder];

    const referralString = localStorage.getItem("referral");
    if (referralString) {
      this.referralData = JSON.parse(referralString);

      if (this.referralData && this.$route.query.referral) {
        if (this.referralData.email_address) {
          this.newUser.username = this.referralData.email_address;
          this.isEmailDisabled = true;
        }
        if (this.referralData.first_name && this.referralData.last_name) {
          this.newUser.firstname = this.referralData.first_name;
          this.newUser.lastname = this.referralData.last_name;
          this.isFirstNameDisabled = true;
          this.isLastNameDisabled = true;
        }

        if (this.referralData.organisation_code) {
          this.newUser.org_code = this.referralData.organisation_code;
          this.fixedCode = true;
        }
      }
    }

    if (this.$route.query.org) {
      //if the org param is in the url, prefill the form and make api call
      this.newUser.org_code = this.$route.query.org;
      this.fixedCode = true;
      this.getOrgLocations();
    } else if (
      this.$store.getters.hardcoded &&
      this.$store.getters.hardcoded.org_code
    ) {
      this.newUser.org_code = this.$store.getters.hardcoded.org_code;
      this.fixedCode = true;
      this.getOrgLocations();
    }

    if (this.$route.query.referral) {
      const code = this.$route.query.referral;
      try {
        const decoded = atob(code);

        this.referralId = JSON.parse(decoded).referral_id;
      } catch (err) {
        console.error("Invalid referral code", err);
      }
    }

    if (isTimezoneSupportEnabled()) {
      this.loadTimezones();
    }
  },
  methods: {
    async loadTimezones() {
      this.isLoadingTimezones = true;
      this.timezoneOptions = await fetchTimezones();
      // Pre-select the build region's zone, never the device clock — auto-detection was
      // rejected because it misfires for travelling users.
      this.selectedTimezone = findTimezoneOption(
        this.timezoneOptions,
        regionalDefaultTimezone(),
      );
      this.isLoadingTimezones = false;
    },
    onTimezoneChange(option) {
      if (option) {
        trackEvent(SIGNUP_TIMEZONE_SELECTED, { value: option.id });
      }
    },
    editPhone(evt) {
      if (isNaN(evt.key) && evt.key !== "Backspace") {
        evt.preventDefault();
      }
      if (evt.key !== "Backspace" && this.mobile && this.mobile.length > 11) {
        evt.preventDefault();
      }
    },
    onOrganisationCodeInput() {
      this.signupTwoFactorEnabled = false;
      this.mobile = null;
      this.isOrgCodeValid = false;
      this.orgLookupGeneration += 1;
    },
    onOrganisationCodeBlur() {
      this.isOrgCodeFocused = false;
      if (
        this.newUser.org_code &&
        !this.isOrgCodeValid &&
        !this.isGetOrgLocationsLoading
      ) {
        this.getOrgLocations();
      }
    },
    isSignupOrgTwoFactorEnabled(value) {
      return value === true || value === 1 || value === "1";
    },
    filterOptions() {
      this.showDropdown = true;
      const search = this.selectedOrganisation?.organisation_name.toLowerCase();
      this.filteredOptions = this.landgOrganizations.filter((option) =>
        option?.organisation_name.toLowerCase().includes(search),
      );
    },
    selectOption(organisation) {
      this.selectedOrganisation = organisation;
      this.showDropdown = false;
      // L&G configures MFA per organisation, not at the client/top level, so the flag
      // lives on the chosen organisation. Reset any mobile entered for a prior choice.
      this.signupTwoFactorEnabled = this.isSignupOrgTwoFactorEnabled(
        organisation.two_factor_enabled,
      );
      this.mobile = null;
    },
    switchForm() {
      this.isSignUp = !this.isSignUp;
    },
    async signUp() {
      if (this.isSignUpLoading) {
        return;
      }
      const isMfaSignup = this.signupTwoFactorEnabled;
      const signupPhone =
        isMfaSignup && this.mobile
          ? ensureE164Phone(String(this.countryCode) + String(this.mobile))
          : "";
      try {
        this.isSignUpLoading = true;
        const payload = {
          username: this.newUser.username,
          firstname: this.newUser.firstname,
          lastname: this.newUser.lastname,
          password: this.newPassword,
          password_repeat: this.newPassword,
          organisation_code: this.newUser.org_code,
          location_id: this.newUser.location,
          member_level: this.newUser.member_level,
          platform: "web",
          lang: this.$store.state.lang,
          landg_org_id: this.selectedOrganisation?.id,
        };
        // Tolerant on the backend: absent or unrecognised stores NULL, never fails signup.
        if (this.selectedTimezone) {
          payload["user_timezone"] = this.selectedTimezone.id;
        }
        if (isMfaSignup && signupPhone) {
          payload["phone"] = signupPhone;
          payload["two_factor_enabled"] = true;
        }
        if (this.referralId) {
          payload["referral_id"] = this.referralId;
        }
        let res = await axios.post(
          config.api_env + "/application/api-hc/signup",
          payload,
          config.options,
        );
        if (isMfaSignup) {
          const mfaResult = await handleSignupMfaResponse({
            signupResponse: res.data,
            phone: signupPhone,
            sendCode: (token, phone) =>
              sendTwoFactorCode(token, phone, (url, body, options) =>
                axios.post(url, body, options),
              ),
          });
          if (mfaResult.handled) {
            await this.$store.dispatch(
              "setTwoFactorChallenge",
              mfaResult.challenge,
            );
            this.$router.push(buildTwoFactorRoute(mfaResult.challenge));
            return;
          }
          if (!res.data.success) {
            this.$store.dispatch("setWarning", res.data.err);
            return;
          }
          if (!res.data.log_in_res || !res.data.user_id) {
            this.$store.dispatch("setWarning", MFA_CHALLENGE_MISSING_MESSAGE);
            return;
          }
        }
        if (res.data.success) {
          const goodMorningBanner =
            res.data?.log_in_res?.user?.banners_display_info
              ?.good_morning_banner;

          localStorage.setItem("goodMorningBanner", goodMorningBanner);
          const user = {
            user_token: res.data.user_id,
            user_info: res.data.log_in_res,
          };
          await this.$store.dispatch(
            "setNavType",
            res.data.log_in_res.home_page,
          );
          const home = await this.$store.dispatch("loginAs", user);
          const marketplaceServiceId = this.referralData
            ?.marketplace_service_id;
          this.referralId && marketplaceServiceId
            ? this.$router.push(`/services/${marketplaceServiceId}`)
            : this.$store.getters.skin === 13
            ? this.$router.push({ name: "insights" })
            : this.$router.push(home);
        } else {
          this.$store.dispatch("setWarning", res.data.err);
        }
      } catch (err) {
        console.error(err);
      } finally {
        localStorage.removeItem("referral");
        this.isSignUpLoading = false;
      }
    },
    async getOrgLocations() {
      if (!this.newUser.org_code) {
        return;
      }
      this.landgOrganizations = [];
      this.organisation = null;
      this.selectedOrganisation = {};
      this.isLandgClient = false;
      this.signupTwoFactorEnabled = false;
      this.mobile = null;
      this.orgLookupGeneration += 1;
      const lookupGeneration = this.orgLookupGeneration;

      let payload = {
        org_code: this.newUser.org_code,
        lang: this.$store.state.lang,
      };
      try {
        this.isGetOrgLocationsLoading = true;
        this.isOrgCodeValid = false;
        let res = await axios.post(
          config.api_env + "/application/api-hc/get-client-signup-options",
          payload,
          config.options,
        );
        if (lookupGeneration !== this.orgLookupGeneration) {
          return;
        }
        if (res.data.success) {
          this.isOrgCodeValid = true;
          if (res.data.landg_client) {
            // L&G maps each organisation to its own client, so two_factor_enabled is
            // absent at the top level and present per organisation instead. The flag is
            // resolved in selectOption once the member picks their organisation.
            this.isLandgClient = true;
            this.landgOrganizations = res.data.landg_organizations;
            this.filteredOptions = this.landgOrganizations;
          } else {
            this.signupTwoFactorEnabled = this.isSignupOrgTwoFactorEnabled(
              res.data.two_factor_enabled,
            );
            this.organisation = res.data;
            if (this.organisation.locations.length > 1) {
              this.newUser.location = null;
              this.locationValid = false;
            } else if (this.organisation.locations.length === 1) {
              this.newUser.location = this.organisation.locations[0].location_id;
              this.locationValid = true;
            }
            if (this.organisation.member_levels.length > 0) {
              this.newUser.member_level = this.organisation.member_levels[0];
            }
          }
        } else {
          this.popUpContent = {
            header: "Oops!",
            message: res.data.err,
            buttonText: "OK",
          };
          this.signUpError = true;
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (lookupGeneration === this.orgLookupGeneration) {
          this.isGetOrgLocationsLoading = false;
        }
      }
    },
    hidePopUp() {
      this.signUpError = false;
    },
    switchVisibility() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
    },
  },
  computed: {
    ...mapGetters(["hardcoded", "skin", "client"]),
    showGoogleSso() {
      return this.client.google_sso_enabled === 1;
    },
    showMsSso() {
      return this.client.ms_sso_enabled === 1;
    },
    validEmail() {
      return this.validateEmailAddress(this.newUser.username);
    },
    signUpDisabled() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return true;
      }
      if (
        this.newUser.terms &&
        this.newUser.firstname.length > 0 &&
        this.newUser.lastname.length > 0 &&
        this.validEmail
      ) {
        if (!this.newUser.org_code || !this.isOrgCodeValid) return true;
        if (this.signupTwoFactorEnabled && !this.mobile) return true;
        if (this.isLandgClient) {
          return !this.selectedOrganisation?.organisation_name;
        } else {
          if (!this.locationValid) return true;
          if (
            this.organisation &&
            this.organisation.member_levels &&
            this.organisation.member_levels.length > 1 &&
            !this.newUser.member_level
          ) {
            //member level must be selected if there's more than one level to choose
            return true;
          }
        }
        return false;
      } else return true;
    },
    getLogo() {
      if (this.hardcoded && this.hardcoded.logo) {
        return this.hardcoded.logo;
      } else return this.$store.getters.client.client_logo;
    },
    privacy() {
      if (this.$store.getters.skin === 8) {
        return "/img/boi/BOI-Wellbeing-Privacy-Policy.pdf";
      } else {
        return "https://www.spectrum.life/privacy/";
      }
    },
    terms() {
      if (this.$store.getters.skin === 8) {
        return "/img/boi/BOI-Wellbeing-TCs.pdf";
      } else {
        return "/terms-and-conditions";
      }
    },
    componentText() {
      return this.getText.notLoggedIn.signUp;
    },
    // AUS builds link to the in-app policy page; all others use the marketing site.
    privacyPolicyLink() {
      return isAustralianApiUrl(process.env.VUE_APP_API_PATH)
        ? "/privacy-policy"
        : "https://www.spectrum.life/privacy/";
    },
  },
  watch: {
    "newUser.location": function(value) {
      this.locationValid =
        this.organisation.locations.length < 1 || value !== null;
    },
  },
};
</script>

<style lang="less">
#UserRegister {
  width: 100%;
  height: 100%;
  min-height: 400px;
  padding: 40px 100px;

  .countryCode {
    font-size: 14px !important;
  }
  .form-control {
    height: auto;
  }
  .custom-select {
    position: relative;
    width: 100%;
    border-radius: 10px;
    font-size: 16px;
    line-height: 15px;
    input {
      display: inline-block;
      font-size: 16px;
      line-height: 15px;
      border: 0;
      width: 100%;
    }
  }
  .custom-dropdown-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid #000;
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .custom-dropdown-list li {
    padding: 18px 14px;
    cursor: pointer;
    font-size: 1.6rem;
  }
  .custom-dropdown-list li:hover {
    background-color: #f8f9fa;
  }
  .custom-org-input {
    position: relative;
    font-size: 2rem;

    .fa-chevron-down {
      position: absolute;
      z-index: 100;
      top: 50%;
      transform: translateY(-50%);
      right: 15px;
    }
  }
  .row {
    margin: 0 !important;
  }
  .title {
    text-align: center;
    font-size: 3.6rem;
    font-family: "Raptor V3 Black";
    margin-bottom: 20px;
  }
  .orDivider {
    margin: 25px 0 15px 0;
    align-items: center;
    justify-content: center;
    display: flex;

    .divider {
      height: 1px;
      width: 43%;
      background: #dcdbdd;
    }
    .or {
      width: 14%;
      text-align: center;
      font-size: 1.5rem;
    }
  }
  .nameHolder {
    display: grid;
    grid-template-columns: 1fr 8px 1fr;
    margin-top: 25px;
    .form-group {
      margin-top: 0 !important;
    }
  }
  .mobileHolder {
    display: grid;
    grid-template-columns: minmax(130px, 0.6fr) 8px 1fr;
    margin-top: 25px;
    align-items: end;
    .form-group {
      margin-top: 0 !important;
    }
    // A <select> and an <input> never derive the same height from `auto`, so pin both
    // to the text inputs' box-model height (15px line + 14px padding·2 + 2px border·2).
    select,
    input {
      height: 47px;
    }
  }
  .validationBox {
    margin-bottom: 15px;
  }
  .form-group {
    position: relative;
    border-radius: 15px;
    margin-top: 25px;
    label {
      padding-left: 10px;
      font-size: 1.5rem;
      line-height: 1.2rem;
      color: #122130;
      margin-bottom: 8px;
    }
    input,
    input:focus,
    select {
      color: #122130;
      border: 2px #000 solid;
      padding: 14px;
      border-radius: 5px;
      font-size: 16px;
      line-height: 15px;
      &.invalid {
        border: 2px darkred solid;
      }

      &:disabled {
        background-color: #eee;
        border: 2px grey solid;
        cursor: not-allowed;
      }
    }
    .password-wrap {
      position: relative;
    }
    .eye {
      cursor: pointer;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 15px;
      font-size: 16px;
    }
  }
  // vue-select renders its own search <input>, which the .form-group rule above would
  // draw as a second bordered box beside the selected value. The border belongs on the
  // toggle instead, so the control reads as one field.
  #input-group-timezone {
    .vs__dropdown-toggle {
      border: 2px #000 solid;
      border-radius: 5px;
      padding: 8px 4px;
      background: #fff;
    }
    .vs__search,
    .vs__search:focus {
      border: 0;
      padding: 0 6px;
      margin: 0;
      font-size: 16px;
      line-height: 15px;
    }
    .vs__selected {
      color: #122130;
      font-size: 16px;
      line-height: 15px;
      margin: 0;
      padding: 0 6px;
    }
    .vs__dropdown-menu {
      font-size: 16px;
    }
    .timezone-loading {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0;
      font-size: 16px;
      color: #122130;
    }
    .timezone-help {
      display: block;
      margin-top: 8px;
      font-size: 1.2rem;
      line-height: 1.4;
      color: #122130;
    }
  }
  .termsHolder {
    grid-template-columns: 40px 1fr;
    margin-top: 20px;
    margin-bottom: 25px;
    display: grid;
    .checkBoxHolder {
      width: 23px;
      height: 23px;
      border: 2px #000 solid;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: auto;
      svg {
        color: white;
      }
      &.accepted {
        background: #000;
      }
    }
    .terms {
      font-size: 1.5rem;
      line-height: 1.9rem;
      text-align: justify;
      margin-left: 20px;
      a {
        background: linear-gradient(90deg, #ed3a75 0%, #6a356a 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        cursor: pointer;
        &:hover {
          background: linear-gradient(90deg, #6a356a 0%, #ed3a75 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          cursor: pointer;
        }
      }
    }
  }
  .new-here-btn {
    background-color: #6d6e70 !important;
    padding: 10px 20px;
    border-radius: 5px;
    max-width: 180px;
    cursor: pointer;
    text-align: center;
    margin: 14px auto 0;
    .new-here-text {
      font-size: 1.4rem;
      font-family: "DMSans Bold";
      color: #fff !important;
      &:hover {
        color: #fff !important;
        text-decoration: none !important;
      }
    }
  }
}
</style>
